// src/services/api.js
import axios from 'axios'
import toast from 'react-hot-toast'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3100/api'

// Create axios instance
const api = axios.create({
    baseURL: API_BASE_URL,
    timeout: 10000,
    headers: {
        'Content-Type': 'application/json'
    }
})

// Request interceptor to add auth token
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('authToken')
        if (token) {
            config.headers.Authorization = `Bearer ${token}`
        }
        return config
    },
    (error) => {
        return Promise.reject(error)
    }
)

// Enhanced response interceptor with better error handling
api.interceptors.response.use(
    (response) => response,
    (error) => {
        const { response } = error

        // Handle different error status codes
        if (response?.status === 401) {
            localStorage.removeItem('authToken')
            localStorage.removeItem('user')
            window.location.href = '/login'
            toast.error('Session expired. Please login again.')
        } else if (response?.status === 403) {
            // Handle specific 403 errors
            if (response.data?.code === 'DEVICE_NOT_ASSOCIATED') {
                toast.error('Device not found in your account. Please associate the device first.')
            } else {
                toast.error('Access denied')
            }
        } else if (response?.status === 429) {
            const retryAfter = response.headers?.['retry-after']
            const waitSec = retryAfter ? parseInt(retryAfter, 10) : null
            const msg = waitSec
                ? `Too many requests. Try again in ${waitSec} seconds.`
                : 'Too many requests. Please slow down.'
            toast.error(msg)
        } else if (response?.status >= 500) {
            toast.error('Server error. Please try again later.')
        } else if (response?.data?.error) {
            // Don't show toast for API errors - let components handle them
            console.error('API Error:', response.data.error)
        } else if (error.code === 'NETWORK_ERROR') {
            toast.error('Network error. Check your connection.')
        }

        return Promise.reject(error)
    }
)

// Auth API (existing)
export const authAPI = {
    login: async (credentials) => {
        const response = await api.post('/login', credentials)
        return response.data
    },

    register: async (userData) => {
        const response = await api.post('/register', userData)
        return response.data
    },

    logout: () => {
        localStorage.removeItem('authToken')
        localStorage.removeItem('user')
    },

    getCurrentUser: () => {
        const user = localStorage.getItem('user')
        return user ? JSON.parse(user) : null
    },

    setCurrentUser: (user) => {
        localStorage.setItem('user', JSON.stringify(user))
    },

    getToken: () => {
        return localStorage.getItem('authToken')
    },

    setAuth: (token, user) => {
        localStorage.setItem('authToken', token)
        localStorage.setItem('user', JSON.stringify(user))
    },

    isAuthenticated: () => {
        return !!localStorage.getItem('authToken')
    }
}

// Tuya Integration API
export const tuyaAPI = {
    configure: async (credentials) => {
        const response = await api.post('/tuya/configure', credentials)
        return response.data
    },

    getStatus: async () => {
        try {
            const response = await api.get('/tuya/status')
            return response.data
        } catch (error) {
            return { configured: false }
        }
    },

    discoverDevices: async () => {
        const response = await api.get('/tuya/discover')
        return response.data
    },

    getDevices: async () => {
        const response = await api.get('/tuya/devices')
        return response.data
    },

    addDevice: async (deviceData) => {
        const response = await api.post('/tuya/devices', deviceData)
        return response.data
    },

    linkDevice: async (linkData) => {
        const response = await api.post('/tuya/link-device', linkData)
        return response.data
    },

    removeDevice: async (deviceId) => {
        const response = await api.delete(`/tuya/devices/${deviceId}`)
        return response.data
    },

    disconnect: async () => {
        const response = await api.delete('/tuya/disconnect')
        return response.data
    }
}

// Gosund Integration API
export const gosundAPI = {
    discover: async (timeout = 10) => {
        const response = await api.get('/gosund/discover', { params: { timeout }, timeout: (timeout + 5) * 1000 })
        return response.data
    },

    getDevices: async () => {
        const response = await api.get('/gosund/devices')
        return response.data
    },

    linkDevice: async (data) => {
        const response = await api.post('/gosund/devices/link', data)
        return response.data
    },

    unlinkDevice: async (deviceId) => {
        const response = await api.delete(`/gosund/devices/${deviceId}/link`)
        return response.data
    },

    control: async (deviceId, state) => {
        const response = await api.post(`/gosund/devices/${deviceId}/control`, { state })
        return response.data
    },

    getStatus: async (deviceId) => {
        const response = await api.get(`/gosund/devices/${deviceId}/status`)
        return response.data
    },

    getEnergy: async (deviceId) => {
        const response = await api.get(`/gosund/devices/${deviceId}/energy`)
        return response.data
    },
}

// Add after existing API endpoints
export const weatherAPI = {
    getCurrentWeather: async (lat, lon) => {
        const response = await api.get(`/weather?lat=${lat}&lon=${lon}`)
        return response.data
    },

    getTemperatureData: async () => {
        const response = await api.get('/temperature-data')
        return response.data
    }
}

export const automationAPI = {
    getRules: async () => {
        const response = await api.get('/automation/rules')
        return response.data
    },

    createRule: async (rule) => {
        const response = await api.post('/automation/rules', rule)
        return response.data
    },

    toggleRule: async (ruleId, enabled) => {
        const response = await api.patch(`/automation/rules/${ruleId}`, { enabled })
        return response.data
    }
}

// SmartRent Integration API
export const smartRentAPI = {
    configure: async (credentials) => {
        const response = await api.post('/smartrent/configure', credentials)
        return response.data
    },

    verify2FA: async (data) => {
        const response = await api.post('/smartrent/verify-2fa', data)
        return response.data
    },

    getStatus: async () => {
        try {
            const response = await api.get('/smartrent/status')
            return response.data
        } catch (error) {
            return { configured: false }
        }
    },

    setFanMode: async (mode) => {
        const response = await api.post('/smartrent/thermostat/set_fan', { mode })
        return response.data
    },

    setMode: async (mode) => {
        const response = await api.post('/smartrent/thermostat/set_mode', { mode })
        return response.data
    },

    setCooling: async (temperature) => {
        const response = await api.post('/smartrent/thermostat/set_cooling', { temperature })
        return response.data
    },

    setHeating: async (temperature) => {
        const response = await api.post('/smartrent/thermostat/set_heating', { temperature })
        return response.data
    },

    lock: async () => {
        const response = await api.post('/smartrent/thermostat/lock')
        return response.data
    },

    unlock: async () => {
        const response = await api.post('/smartrent/thermostat/unlock')
        return response.data
    },

    monitor: async (autoTemp, onTemp) => {
        const response = await api.post('/smartrent/thermostat/monitor', { autoTemp, onTemp })
        return response.data
    },

    disconnect: async () => {
        const response = await api.delete('/smartrent/disconnect')
        return response.data
    }
}

// Schedule API
export const scheduleAPI = {
    getSchedules: async () => {
        const response = await api.get('/schedules')
        return response.data
    },

    createSchedule: async (schedule) => {
        const response = await api.post('/schedules', schedule)
        return response.data
    },

    updateSchedule: async (scheduleId, updates) => {
        const response = await api.patch(`/schedules/${scheduleId}`, updates)
        return response.data
    },

    testSchedule: async (scheduleId) => {
        const response = await api.post(`/schedules/${scheduleId}/test`)
        return response.data
    },

    deleteSchedule: async (scheduleId) => {
        const response = await api.delete(`/schedules/${scheduleId}`)
        return response.data
    }
}

// Smart Thermostat API
export const smartThermostatAPI = {
    getStatus: async () => {
        const response = await api.get('/smart-thermostat/status')
        return response.data
    },

    getSchedules: async () => {
        const response = await api.get('/smart-thermostat/schedules')
        return response.data
    },

    createSchedule: async (schedule) => {
        const response = await api.post('/smart-thermostat/schedules', schedule)
        return response.data
    },

    updateSchedule: async (scheduleId, updates) => {
        const response = await api.patch(`/smart-thermostat/schedules/${scheduleId}`, updates)
        return response.data
    },

    deleteSchedule: async (scheduleId) => {
        const response = await api.delete(`/smart-thermostat/schedules/${scheduleId}`)
        return response.data
    },

    forceEvaluate: async () => {
        const response = await api.post('/smart-thermostat/evaluate')
        return response.data
    },

    getActionLog: async (limit = 50) => {
        const response = await api.get(`/smart-thermostat/log?limit=${limit}`)
        return response.data
    },

    override: async (mode) => {
        const response = await api.post('/smart-thermostat/override', { mode })
        return response.data
    }
}

// Fan Temp Control API
export const fanTempAPI = {
    start: async (params) => {
        const response = await api.post('/fan-temp/start', params)
        return response.data
    },

    stop: async () => {
        const response = await api.post('/fan-temp/stop')
        return response.data
    },

    getStatus: async () => {
        const response = await api.get('/fan-temp/status')
        return response.data
    },

    getHistory: async (limit = 20) => {
        const response = await api.get(`/fan-temp/history?limit=${limit}`)
        return response.data
    }
}

// Enhanced Device API with association and better error handling
export const deviceAPI = {
    getDevices: async () => {
        try {
            const response = await api.get('/devices')
            return response.data
        } catch (error) {
            console.error('Failed to fetch devices:', error)
            throw error
        }
    },

    // NEW: Associate device with user account
    associateDevice: async (mac) => {
        try {
            const response = await api.post('/devices/associate', { mac })
            return response.data
        } catch (error) {
            console.error('Failed to associate device:', error)
            throw error
        }
    },

    triggerDevice: async (deviceData) => {
        try {
            const response = await api.post('/trigger', deviceData)
            return response.data
        } catch (error) {
            console.error('Failed to trigger device:', error)
            throw error
        }
    },

    triggerGlobal: async (commandData) => {
        try {
            const response = await api.post('/triggerGlobal', commandData)
            return response.data
        } catch (error) {
            console.error('Failed to trigger global command:', error)
            throw error
        }
    },

    toggleRelay: async (mac) => {
        try {
            const response = await api.post('/toggleRelay', { mac })
            return response.data
        } catch (error) {
            console.error('Failed to toggle relay:', error)
            throw error
        }
    },

    removeDevice: async (mac) => {
        try {
            const response = await api.post('/removeDevice', { mac })
            return response.data
        } catch (error) {
            console.error('Failed to remove device:', error)
            throw error
        }
    },

    getDeviceHistory: async (mac, page = 1, limit = 50) => {
        try {
            const response = await api.get(`/devices/${mac}/history?page=${page}&limit=${limit}`)
            return response.data
        } catch (error) {
            console.error('Failed to fetch device history:', error)
            throw error
        }
    },

    emergencyStop: async (mac = null) => {
        try {
            const response = await api.post('/emergency-stop', mac ? { mac } : {})
            return response.data
        } catch (error) {
            console.error('Failed to activate emergency stop:', error)
            throw error
        }
    },

    setLock: async (mac, locked) => {
        const response = await api.post(`/devices/${mac}/lock`, { locked })
        return response.data
    },

    setOpeningLink: async (mac, link) => {
        const response = await api.put(`/devices/${mac}/opening-link`, link)
        return response.data
    }
}

// NEW: Hub API
export const hubAPI = {
    getHubs: async () => {
        try {
            const response = await api.get('/hubs')
            return response.data
        } catch (error) {
            console.error('Failed to fetch hubs:', error)
            throw error
        }
    },

    registerHub: async (credentials) => {
        try {
            const response = await api.post('/hubs/register', credentials)
            return response.data
        } catch (error) {
            console.error('Failed to register hub:', error)
            throw error
        }
    },

    discoverHubs: async () => {
        try {
            const response = await api.post('/hubs/discover')
            return response.data
        } catch (error) {
            console.error('Failed to discover hubs:', error)
            throw error
        }
    },

    controlDevice: async (hubId, deviceMac, action, pin, duration) => {
        try {
            const response = await api.post(`/hubs/${hubId}/control`, {
                action,
                deviceMac,
                pin,
                duration
            })
            return response.data
        } catch (error) {
            console.error('Failed to control device via hub:', error)
            throw error
        }
    },

    emergencyStop: async (hubId, deviceMac = null) => {
        try {
            const response = await api.post(`/hubs/${hubId}/emergency-stop`,
                deviceMac ? { deviceMac } : {}
            )
            return response.data
        } catch (error) {
            console.error('Failed to activate hub emergency stop:', error)
            throw error
        }
    },

    getPinConfig: async () => {
        try {
            const response = await api.get('/pins/config')
            return response.data
        } catch (error) {
            console.error('Failed to fetch pin configuration:', error)
            throw error
        }
    }
}

// Simulator API - CFD Floorplan Simulator
export const simulatorAPI = {
    getFloorplans: async () => {
        const response = await api.get('/simulator/floorplans')
        return response.data
    },

    getFloorplan: async (id) => {
        const response = await api.get(`/simulator/floorplans/${id}`)
        return response.data
    },

    createFloorplan: async (data) => {
        const response = await api.post('/simulator/floorplans', data)
        return response.data
    },

    updateFloorplan: async (id, data) => {
        const response = await api.put(`/simulator/floorplans/${id}`, data)
        return response.data
    },

    deleteFloorplan: async (id) => {
        const response = await api.delete(`/simulator/floorplans/${id}`)
        return response.data
    }
}

// Digital Twin API - Multi-floor real-time home model
export const digitalTwinAPI = {
    getConfigs: async () => {
        const response = await api.get('/digital-twin')
        return response.data
    },
    getConfig: async (id) => {
        const response = await api.get(`/digital-twin/${id}`)
        return response.data
    },
    createConfig: async (data) => {
        const response = await api.post('/digital-twin', data)
        return response.data
    },
    updateConfig: async (id, data) => {
        const response = await api.put(`/digital-twin/${id}`, data)
        return response.data
    },
    deleteConfig: async (id) => {
        const response = await api.delete(`/digital-twin/${id}`)
        return response.data
    },
    addFloor: async (id, floorData) => {
        const response = await api.post(`/digital-twin/${id}/floors`, floorData)
        return response.data
    },
    removeFloor: async (id, floorIdx) => {
        const response = await api.delete(`/digital-twin/${id}/floors/${floorIdx}`)
        return response.data
    },
    updateSensorMappings: async (id, floorIdx, mappings) => {
        const response = await api.put(`/digital-twin/${id}/floors/${floorIdx}/sensors`, { sensorMappings: mappings })
        return response.data
    },
    saveOptimization: async (id, optimization) => {
        const response = await api.put(`/digital-twin/${id}/optimization`, optimization)
        return response.data
    },
    getLiveData: async (id) => {
        const response = await api.get(`/digital-twin/${id}/live-data`)
        return response.data
    },
}

// Weather Forecast API - Real forecast data for digital twin
export const weatherForecastAPI = {
    getCurrent: async (lat, lon) => {
        const response = await api.get(`/weather/current?lat=${lat}&lon=${lon}`)
        return response.data
    },
    getForecast: async (lat, lon, hours = 24) => {
        const response = await api.get(`/weather/forecast?lat=${lat}&lon=${lon}&hours=${hours}`)
        return response.data
    },
    getDailyForecast: async (lat, lon, days = 7) => {
        const response = await api.get(`/weather/daily?lat=${lat}&lon=${lon}&days=${days}`)
        return response.data
    },
    getAlerts: async (lat, lon) => {
        const response = await api.get(`/weather/alerts?lat=${lat}&lon=${lon}`)
        return response.data
    },
    getPredictions: async (lat, lon, indoorTemp, targetTemp, hours = 6) => {
        const response = await api.get(`/weather/predictions?lat=${lat}&lon=${lon}&indoorTemp=${indoorTemp}&targetTemp=${targetTemp}&hours=${hours}`)
        return response.data
    },
    recordOverride: async (recommendation, action) => {
        const response = await api.post('/weather/predictions/override', { recommendation, action })
        return response.data
    },
}

// Air Quality API - current AQI + auto-close settings
export const airQualityAPI = {
    getCurrent: async (lat, lon) => {
        const params = lat != null && lon != null ? `?lat=${lat}&lon=${lon}` : ''
        const response = await api.get(`/air-quality/current${params}`)
        return response.data
    },
    updateSettings: async (settings) => {
        const response = await api.put('/air-quality/settings', settings)
        return response.data
    },
}

// Openings API - window/door status and control
export const openingsAPI = {
    getStatus: async () => {
        const response = await api.get('/openings/status')
        return response.data
    },
    openAll: async (types = ['window', 'door'], amount = 'full') => {
        const response = await api.post('/openings/open', { types, amount })
        return response.data
    },
    closeAll: async (types = ['window', 'door'], amount = 'full') => {
        const response = await api.post('/openings/close', { types, amount })
        return response.data
    },
    updateHvacGuard: async (settings) => {
        const response = await api.put('/openings/hvac-guard', settings)
        return response.data
    },
}

// Smart Home Accounts API (multi-provider)
export const smartHomeAccountsAPI = {
    getAccounts: async () => {
        const response = await api.get('/smart-home-accounts')
        return response.data
    },

    addAccount: async (accountData) => {
        const response = await api.post('/smart-home-accounts', accountData)
        return response.data
    },

    updateAccount: async (accountId, updates) => {
        const response = await api.put(`/smart-home-accounts/${accountId}`, updates)
        return response.data
    },

    removeAccount: async (accountId) => {
        const response = await api.delete(`/smart-home-accounts/${accountId}`)
        return response.data
    },
}

// Two-Factor Authentication API
export const twoFactorAPI = {
    enable: async () => {
        const response = await api.post('/2fa/enable')
        return response.data
    },

    confirmEnable: async (code) => {
        const response = await api.post('/2fa/confirm-enable', { code })
        return response.data
    },

    disable: async (password) => {
        const response = await api.post('/2fa/disable', { password })
        return response.data
    },

    verifyLogin: async (userId, code, clientFingerprint) => {
        const response = await api.post('/2fa/verify-login', { userId, code, clientFingerprint, trustDevice: true })
        return response.data
    },
}

// Device Tracking API (trusted browser devices)
export const deviceTrackingAPI = {
    listDevices: async () => {
        const response = await api.get('/devices/trusted')
        return response.data
    },
    updateDevice: async (deviceId, data) => {
        const response = await api.put(`/devices/trusted/${deviceId}`, data)
        return response.data
    },
    removeDevice: async (deviceId) => {
        const response = await api.delete(`/devices/trusted/${deviceId}`)
        return response.data
    },
    trustCurrent: async (clientFingerprint) => {
        const response = await api.post('/devices/trusted/trust-current', { clientFingerprint })
        return response.data
    },
}

// Push Notification API
export const pushAPI = {
    getVapidKey: async () => {
        const response = await api.get('/push/vapid-key')
        return response.data
    },

    subscribe: async (subscription, userAgent) => {
        const response = await api.post('/push/subscribe', { subscription, userAgent })
        return response.data
    },

    unsubscribe: async (endpoint) => {
        const response = await api.post('/push/unsubscribe', { endpoint })
        return response.data
    },

    test: async () => {
        const response = await api.post('/push/test')
        return response.data
    },

    getStatus: async () => {
        const response = await api.get('/push/status')
        return response.data
    },
}

// User API (existing)
export const userAPI = {
    getProfile: async () => {
        const response = await api.get('/user/profile')
        return response.data
    },

    updateProfile: async (profileData) => {
        const response = await api.put('/user/profile', profileData)
        return response.data
    },

    changePassword: async (passwordData) => {
        const response = await api.post('/user/change-password', passwordData)
        return response.data
    }
}

// Billing API (Stripe)
export const billingAPI = {
    getPlans: async () => {
        const response = await api.get('/billing/plans')
        return response.data
    },

    createCheckout: async (plan) => {
        const response = await api.post('/billing/create-checkout', { plan })
        return response.data
    },

    createPortal: async () => {
        const response = await api.post('/billing/create-portal')
        return response.data
    },
}

// Sensor Data API
export const sensorDataAPI = {
    ingest: async (deviceMac, readings, source = 'manual') => {
        const response = await api.post('/sensor-data/ingest', { deviceMac, readings, source })
        return response.data
    },

    query: async (deviceMac, { start, end, resolution } = {}) => {
        const params = {}
        if (start) params.start = start
        if (end) params.end = end
        if (resolution) params.resolution = resolution
        const response = await api.get(`/sensor-data/${deviceMac}`, { params })
        return response.data
    },

    summary: async (deviceMac, hours = 24) => {
        const response = await api.get(`/sensor-data/${deviceMac}/summary`, { params: { hours } })
        return response.data
    },
}

// Admin API
export const adminAPI = {
    getStats: async () => {
        const response = await api.get('/admin/stats')
        return response.data
    },
    getUsers: async (params) => {
        const response = await api.get('/admin/users', { params })
        return response.data
    },
    getDevices: async (params) => {
        const response = await api.get('/admin/devices', { params })
        return response.data
    },
    toggleUserStatus: async (userId) => {
        const response = await api.post(`/admin/users/${userId}/toggle-status`)
        return response.data
    },
    setUserRole: async (userId, role) => {
        const response = await api.patch(`/admin/users/${userId}/role`, { role })
        return response.data
    },
    getUserAnalytics: async (days = 30) => {
        const response = await api.get('/admin/analytics/users', { params: { days } })
        return response.data
    },
    getDeviceAnalytics: async (days = 30, filters = {}) => {
        const response = await api.get('/admin/analytics/devices', { params: { days, ...filters } })
        return response.data
    },
    getCommandAnalytics: async (days = 30, filters = {}) => {
        const response = await api.get('/admin/analytics/commands', { params: { days, ...filters } })
        return response.data
    },
    getTickets: async (params) => {
        const response = await api.get('/admin/tickets', { params })
        return response.data
    },
    updateTicket: async (ticketId, updates) => {
        const response = await api.patch(`/admin/tickets/${ticketId}`, updates)
        return response.data
    },
    replyToTicket: async (ticketId, message) => {
        const response = await api.post(`/admin/tickets/${ticketId}/reply`, { message })
        return response.data
    },
    getLeads: async (params) => {
        const response = await api.get('/admin/leads', { params })
        return response.data
    },
    updateLead: async (leadId, updates) => {
        const response = await api.patch(`/admin/leads/${leadId}`, updates)
        return response.data
    },
    getSupportStaff: async () => {
        const response = await api.get('/admin/support-staff')
        return response.data
    },
}

// Support Ticket API (user-facing)
export const supportAPI = {
    createTicket: async (data) => {
        const response = await api.post('/support/tickets', data)
        return response.data
    },
    getMyTickets: async (params) => {
        const response = await api.get('/support/tickets/mine', { params })
        return response.data
    },
    getTicket: async (ticketId) => {
        const response = await api.get(`/support/tickets/${ticketId}`)
        return response.data
    },
    reply: async (ticketId, message) => {
        const response = await api.post(`/support/tickets/${ticketId}/reply`, { message })
        return response.data
    },
}

// Lead API (public, no auth)
export const leadAPI = {
    submit: async (data) => {
        const response = await api.post('/leads', data)
        return response.data
    },
}

// API Key management
export const apiKeyAPI = {
    create: async (data) => {
        const response = await api.post('/api-keys', data)
        return response.data
    },
    list: async () => {
        const response = await api.get('/api-keys')
        return response.data
    },
    revoke: async (keyId) => {
        const response = await api.delete(`/api-keys/${keyId}`)
        return response.data
    },
}

// Account Members API
export const accountAPI = {
    getMembers: async () => {
        const response = await api.get('/account/members')
        return response.data
    },
    addMember: async (data) => {
        const response = await api.post('/account/members', data)
        return response.data
    },
    removeMember: async (memberId) => {
        const response = await api.delete(`/account/members/${memberId}`)
        return response.data
    },
    updateMember: async (memberId, data) => {
        const response = await api.patch(`/account/members/${memberId}`, data)
        return response.data
    },
}

// Energy Analytics API
export const energyAPI = {
    getSummary: async (period) => {
        const response = await api.get('/energy/summary', { params: { period } })
        return response.data
    },
    getByDevice: async (start, end) => {
        const response = await api.get('/energy/by-device', { params: { start, end } })
        return response.data
    },
    getDaily: async (start, end) => {
        const response = await api.get('/energy/daily', { params: { start, end } })
        return response.data
    },
    getSavings: async () => {
        const response = await api.get('/energy/savings')
        return response.data
    },
    setRates: async (rate) => {
        const response = await api.post('/energy/rates', { rate })
        return response.data
    },
}

// Device Status API
export const deviceStatusAPI = {
    getLog: async (mac, limit = 50) => {
        const response = await api.get(`/devices/${mac}/status-log`, { params: { limit } })
        return response.data
    },
    updateStatus: async (mac, data) => {
        const response = await api.post(`/devices/${mac}/status`, data)
        return response.data
    },
}

// Scene / Automation API
export const sceneAPI = {
    list: async () => {
        const response = await api.get('/scenes')
        return response.data
    },

    create: async (scene) => {
        const response = await api.post('/scenes', scene)
        return response.data
    },

    update: async (id, data) => {
        const response = await api.put(`/scenes/${id}`, data)
        return response.data
    },

    delete: async (id) => {
        const response = await api.delete(`/scenes/${id}`)
        return response.data
    },

    trigger: async (id) => {
        const response = await api.post(`/scenes/${id}/trigger`)
        return response.data
    },

    getPresets: async () => {
        const response = await api.get('/scenes/presets')
        return response.data
    },
}

export default api